import { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { useNavigate, Link } from "react-router-dom";
const MapPicker = lazy(() => import("@/components/MapPicker"));
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Zap,
  ArrowLeft,
  ArrowRight,
  MapPin,
  Building2,
  Pencil,
  Sun,
  Check,
  Plus,
  Trash2,
  Lock,
} from "lucide-react";
import SiteLayoutMap from "@/components/SiteLayoutMap";
import { generateLiveLayout } from "@/utils/generateLiveLayout";

// ── Constants ────────────────────────────────────────────────────────────────

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Chandigarh", "Puducherry",
];

const CHARGER_OPTIONS = [
  { id: "30kW_DC",  label: "30 kW DC Fast",       power: 30,  cost_range: "₹6-9L"    },
  { id: "60kW_DC",  label: "60 kW DC Fast",       power: 60,  cost_range: "₹12-18L"  },
  { id: "120kW_DC", label: "120 kW DC Fast",      power: 120, cost_range: "₹20-28L"  },
  { id: "150kW_DC", label: "150 kW Supercharger", power: 150, cost_range: "₹24-32L"  },
  { id: "22kW_AC",  label: "22 kW AC Slow",       power: 22,  cost_range: "₹0.8-1.2L"},
];

// Dot colors matching SiteLayoutMap power-based charger colors
const CHARGER_TYPE_COLORS = {
  "22kW_AC":  "#16a34a",
  "30kW_DC":  "#0ea5e9",
  "60kW_DC":  "#2563eb",
  "120kW_DC": "#4f46e5",
  "150kW_DC": "#7c3aed",
};

const UNIT_FACTORS = { meters: 1, feet: 0.3048, yards: 0.9144 };

// ── Helpers ───────────────────────────────────────────────────────────────────

const toMeters = (v, unit) => parseFloat(v) * (UNIT_FACTORS[unit] || 1);

/** Merge SiteLayoutMap positions back into the full layout object */
function buildFrozenLayout(layout, positions) {
  if (!layout || !positions) return layout;

  const origSolarArea =
    layout.solar?.area_sqm ||
    (layout.solar?.position?.width  ?? 0) * (layout.solar?.position?.height ?? 0);

  const newSolarArea = positions.solar
    ? positions.solar.width * positions.solar.height
    : origSolarArea;

  const newKwp =
    positions.solar && layout.solar?.estimated_kWp && origSolarArea > 0
      ? parseFloat(((layout.solar.estimated_kWp / origSolarArea) * newSolarArea).toFixed(1))
      : layout.solar?.estimated_kWp;

  return {
    ...layout,
    charger_arrangement: {
      ...layout.charger_arrangement,
      positions: positions.chargers,
    },
    parking: {
      ...layout.parking,
      count:     positions.parking.length,
      positions: positions.parking,
    },
    buildings: positions.buildings,
    solar: layout.solar
      ? {
          ...layout.solar,
          position:      positions.solar || layout.solar.position,
          area_sqm:      parseFloat(newSolarArea.toFixed(1)),
          estimated_kWp: newKwp,
        }
      : layout.solar,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const NewProject = () => {
  const navigate   = useNavigate();
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name:          "",
    description:   "",
    land_length:   "",
    land_width:    "",
    land_unit:     "meters",
    land_shape:    "rectangular",
    access_points: "1",
    location_type: "urban",
    state:         "",
    pin_code:      "",
    features:      [],
    charger_config:  [{ type: "60kW_DC", quantity: 4 }],
    solar_required:  true,
    budget_range:    "",
    land_lease_cost_monthly: "",
  });

  // Positions tracked from SiteLayoutMap — updated on every drag-end or parking change
  const frozenPositionsRef = useRef(null);

  // Canvas reset state — incremented when dimensions change significantly (>15%)
  const [canvasKey, setCanvasKey] = useState(0);
  const prevDimsRef = useRef({ l: 0, w: 0 });

  // ── Derived / memoised ────────────────────────────────────────────────────

  const lengthInMeters = useMemo(
    () => toMeters(formData.land_length, formData.land_unit),
    [formData.land_length, formData.land_unit],
  );
  const widthInMeters = useMemo(
    () => toMeters(formData.land_width, formData.land_unit),
    [formData.land_width, formData.land_unit],
  );

  /** Max charger UNITS that physically fit given the current site dimensions.
   *  Each unit = 1 dual-gun pedestal = 2 bays (pairWidth 7.4m + 1.5m gap = 8.9m per slot). */
  const maxFittingChargers = useMemo(() => {
    if (!lengthInMeters || !widthInMeters) return 4;
    const margin = 3;
    const pairWidth = 7.4;        // 2 bays × 3.5m + 0.4m internal gap
    const pairExternalGap = 1.5;  // gap between charger units
    const usableL = lengthInMeters - 2 * margin;
    const usableW = widthInMeters - 2 * margin;
    if (usableL < pairWidth || usableW < 6) return 1;
    const perRow = Math.max(1, Math.floor(usableL / (pairWidth + pairExternalGap)));
    const rows = usableW > 20 ? 2 : 1;
    return Math.max(1, perRow * rows);
  }, [lengthInMeters, widthInMeters]);

  /** Whether the site is large enough for a solar canopy (min 10m × 10m) */
  const solarFeasible = useMemo(
    () => !lengthInMeters || !widthInMeters || (lengthInMeters >= 10 && widthInMeters >= 10),
    [lengthInMeters, widthInMeters],
  );

  // Auto-clamp charger quantities when dimensions change so total ≤ maxFittingChargers
  useEffect(() => {
    if (!lengthInMeters || !widthInMeters) return;
    setFormData(prev => {
      const total = prev.charger_config.reduce((s, c) => s + (c.quantity || 0), 0);
      if (total <= maxFittingChargers) return prev;
      let rem = maxFittingChargers;
      const adjusted = prev.charger_config.map((c, i, arr) => {
        const q = Math.max(1, Math.min(c.quantity || 1, rem - (arr.length - 1 - i)));
        rem = Math.max(0, rem - q);
        return { ...c, quantity: q };
      });
      return { ...prev, charger_config: adjusted };
    });
  }, [lengthInMeters, widthInMeters, maxFittingChargers]);

  // Auto-uncheck solar if the site is too small
  useEffect(() => {
    if (lengthInMeters && widthInMeters && !solarFeasible) {
      setFormData(prev => prev.solar_required ? { ...prev, solar_required: false } : prev);
    }
  }, [solarFeasible, lengthInMeters, widthInMeters]);

  /** Live layout — regenerates whenever any design input changes */
  const liveLayout = useMemo(() => {
    if (!lengthInMeters || !widthInMeters) return null;
    return generateLiveLayout({
      ...formData,
      land_length: lengthInMeters,
      land_width:  widthInMeters,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lengthInMeters, widthInMeters,
    formData.charger_config,
    formData.features,
    formData.solar_required,
    formData.location_type,
  ]);

  const getTotalChargers = () =>
    formData.charger_config.reduce((s, c) => s + (c.quantity || 0), 0);

  const getTotalPower = () =>
    formData.charger_config.reduce((s, c) => {
      const info = CHARGER_OPTIONS.find(o => o.id === c.type);
      return s + (info?.power || 0) * (c.quantity || 0);
    }, 0);

  // ── Step navigation ───────────────────────────────────────────────────────

  const handleNext = () => {
    if (step === 2) {
      const { l: prevL, w: prevW } = prevDimsRef.current;
      const sigChange =
        prevL > 0 &&
        prevW > 0 &&
        (Math.abs(lengthInMeters - prevL) / prevL > 0.15 ||
         Math.abs(widthInMeters - prevW) / prevW > 0.15);
      if (sigChange) {
        frozenPositionsRef.current = null;
        setCanvasKey(k => k + 1);
      }
      prevDimsRef.current = { l: lengthInMeters, w: widthInMeters };
    }
    setStep(step + 1);
  };

  // ── Form handlers ─────────────────────────────────────────────────────────

  const handleInputChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const toggleFeature = feature =>
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature],
    }));

  const addChargerConfig = () => {
    const usedTypes = formData.charger_config.map(c => c.type);
    const available = CHARGER_OPTIONS.find(o => !usedTypes.includes(o.id));
    if (available) {
      setFormData(prev => ({
        ...prev,
        charger_config: [...prev.charger_config, { type: available.id, quantity: 2 }],
      }));
    }
  };

  const removeChargerConfig = index => {
    if (formData.charger_config.length > 1) {
      setFormData(prev => ({
        ...prev,
        charger_config: prev.charger_config.filter((_, i) => i !== index),
      }));
    }
  };

  const updateChargerConfig = (index, field, value) =>
    setFormData(prev => ({
      ...prev,
      charger_config: prev.charger_config.map((c, i) =>
        i === index
          ? { ...c, [field]: field === "quantity" ? parseInt(value) || 1 : value }
          : c
      ),
    }));

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!formData.name || !formData.land_length || !formData.land_width) {
      toast.error("Please fill in required fields");
      return;
    }
    if (getTotalChargers() === 0) {
      toast.error("Please add at least one charger");
      return;
    }

    // Build the frozen initial layout from latest positions
    const initial_layout = buildFrozenLayout(liveLayout, frozenPositionsRef.current);

    setLoading(true);
    try {
      const response = await fetch(`${API}/projects`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name:        formData.name,
          description: formData.description,
          initial_layout,
          site_inputs: {
            land_length:    lengthInMeters,
            land_width:     widthInMeters,
            land_unit:      formData.land_unit,
            original_length: parseFloat(formData.land_length),
            original_width:  parseFloat(formData.land_width),
            land_shape:     formData.land_shape,
            access_points:  parseInt(formData.access_points),
            location_type:  formData.location_type,
            state:          formData.state     || null,
            pin_code:       formData.pin_code  || null,
            latitude:       formData.latitude  ? parseFloat(formData.latitude)  : null,
            longitude:      formData.longitude ? parseFloat(formData.longitude) : null,
            features:       formData.features,
            charger_config: formData.charger_config,
            total_chargers: getTotalChargers(),
            total_power_kw: getTotalPower(),
            solar_required: formData.solar_required,
            budget_range:   formData.budget_range || null,
            land_lease_cost_monthly: formData.land_lease_cost_monthly ? parseFloat(formData.land_lease_cost_monthly) : null,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Project created! Layout frozen — ready for analysis.");
        navigate(`/project/${data.project_id}`);
      } else {
        toast.error("Failed to create project");
      }
    } catch {
      toast.error("Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  // ── Step metadata ─────────────────────────────────────────────────────────

  const steps = [
    { num: 1, title: "Basic Info",    icon: MapPin      },
    { num: 2, title: "Site Details",  icon: Building2   },
    { num: 3, title: "Design",        icon: Pencil      },
  ];

  const featureOptions = [
    { id: "cafe",      label: "Café / Restaurant"  },
    { id: "store",     label: "Convenience Store"  },
    { id: "car_wash",  label: "Car Wash"            },
    { id: "restrooms", label: "Restrooms"           },
  ];

  // Step 3 needs a wide container — override the max-w here
  const isDesignStep = step === 3;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f2ec" }} data-testid="new-project-page">
      {/* Header */}
      <header style={{ backgroundColor: "#504136", borderBottom: "1px solid #5e4e41" }} className="sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 mr-6 transition-colors"
              style={{ color: "#B2E6D4" }}
              onMouseEnter={e => e.currentTarget.style.color = "#faf9f6"}
              onMouseLeave={e => e.currentTarget.style.color = "#B2E6D4"}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#689689" }}>
                <Zap className="w-5 h-5" style={{ color: "#faf9f6" }} />
              </div>
              <span className="font-bold text-lg" style={{ color: "#faf9f6" }}>New Project</span>
            </div>
          </div>
        </div>
      </header>

      <main className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isDesignStep ? "max-w-screen-2xl" : "container"}`}>
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div
                className="step-indicator"
                style={{
                  backgroundColor: step > s.num ? "#689689" : step === s.num ? "#689689" : "#B2E6D4",
                  color: step > s.num || step === s.num ? "#faf9f6" : "#4a7068",
                }}
              >
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span
                className="hidden sm:inline ml-2 text-sm"
                style={{
                  color: step >= s.num ? "#504136" : "#4a7068",
                  fontWeight: step >= s.num ? 500 : 400,
                }}
              >
                {s.title}
              </span>
              {i < steps.length - 1 && (
                <div
                  className="w-8 sm:w-16 h-0.5 mx-2"
                  style={{ backgroundColor: step > s.num ? "#689689" : "#B2E6D4" }}
                />
              )}
            </div>
          ))}
        </div>

        {/* ── Steps 1 & 2 — narrow card ──────────────────────────────────── */}
        {!isDesignStep && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
          <Card className="max-w-2xl mx-auto" style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
            <CardHeader>
              <CardTitle className="text-xl" style={{ color: "#504136", fontFamily: "Chivo, sans-serif" }}>
                {steps[step - 1].title}
              </CardTitle>
              <CardDescription style={{ color: "#4a7068" }}>
                {step === 1 && "Enter basic project information"}
                {step === 2 && "Describe your land and location"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ── Step 1: Basic Info ── */}
              {step === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name" style={{ color: "#504136" }}>Project Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Highway Station - NH48"
                      value={formData.name}
                      onChange={e => handleInputChange("name", e.target.value)}
                      style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description" style={{ color: "#504136" }}>Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Brief description of the project..."
                      value={formData.description}
                      onChange={e => handleInputChange("description", e.target.value)}
                      rows={3}
                      style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                      data-testid="project-description-input"
                    />
                  </div>
                </>
              )}

              {/* ── Step 2: Site Details ── */}
              {step === 2 && (
                <>
                  {/* Map Picker */}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium" style={{ color: "#504136" }}>Pin Your Site on the Map</Label>
                    <p className="text-xs -mt-1" style={{ color: "#4a7068" }}>
                      Search an address or click the map — state &amp; PIN code will auto-fill
                    </p>
                    <Suspense fallback={
                      <div className="h-56 rounded-xl flex items-center justify-center text-sm border" style={{ backgroundColor: "#edf8f4", color: "#4a7068", borderColor: "#B2E6D4" }}>
                        Loading map…
                      </div>
                    }>
                      <MapPicker
                        onLocationSelect={({ lat, lng, state, pin_code }) => {
                          if (state)    handleInputChange("state",    state);
                          if (pin_code) handleInputChange("pin_code", pin_code);
                          if (lat)      handleInputChange("latitude",  lat);
                          if (lng)      handleInputChange("longitude", lng);
                        }}
                      />
                    </Suspense>
                  </div>

                  {/* PIN + State */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pin_code" style={{ color: "#504136" }}>PIN Code</Label>
                      <Input
                        id="pin_code"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="e.g. 400001"
                        value={formData.pin_code}
                        onChange={e =>
                          handleInputChange("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                        data-testid="pin-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: "#504136" }}>State *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={v => handleInputChange("state", v)}
                      >
                        <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDIAN_STATES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="space-y-2">
                    <Label style={{ color: "#504136" }}>Measurement Unit</Label>
                    <Select
                      value={formData.land_unit}
                      onValueChange={v => handleInputChange("land_unit", v)}
                    >
                      <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="land-unit-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meters">Meters (m)</SelectItem>
                        <SelectItem value="feet">Feet (ft)</SelectItem>
                        <SelectItem value="yards">Yards (yd)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="land_length" style={{ color: "#504136" }}>Land Length ({formData.land_unit}) *</Label>
                      <Input
                        id="land_length"
                        type="number"
                        placeholder={formData.land_unit === "meters" ? "e.g., 80" : formData.land_unit === "feet" ? "e.g., 262" : "e.g., 87"}
                        value={formData.land_length}
                        onChange={e => handleInputChange("land_length", e.target.value)}
                        style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                        data-testid="land-length-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="land_width" style={{ color: "#504136" }}>Land Width ({formData.land_unit}) *</Label>
                      <Input
                        id="land_width"
                        type="number"
                        placeholder={formData.land_unit === "meters" ? "e.g., 50" : formData.land_unit === "feet" ? "e.g., 164" : "e.g., 55"}
                        value={formData.land_width}
                        onChange={e => handleInputChange("land_width", e.target.value)}
                        style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                        data-testid="land-width-input"
                      />
                    </div>
                  </div>

                  {/* Shape + Access */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label style={{ color: "#504136" }}>Land Shape</Label>
                      <Select
                        value={formData.land_shape}
                        onValueChange={v => handleInputChange("land_shape", v)}
                      >
                        <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="land-shape-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rectangular">Rectangular</SelectItem>
                          <SelectItem value="L-shaped">L-Shaped</SelectItem>
                          <SelectItem value="triangular">Triangular</SelectItem>
                          <SelectItem value="irregular">Irregular</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: "#504136" }}>Access Points</Label>
                      <Select
                        value={formData.access_points}
                        onValueChange={v => handleInputChange("access_points", v)}
                      >
                        <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="access-points-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Access Point</SelectItem>
                          <SelectItem value="2">2 Access Points</SelectItem>
                          <SelectItem value="3">3+ Access Points</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label style={{ color: "#504136" }}>Location Type</Label>
                    <Select
                      value={formData.location_type}
                      onValueChange={v => handleInputChange("location_type", v)}
                    >
                      <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="location-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urban">Urban</SelectItem>
                        <SelectItem value="highway">Highway</SelectItem>
                        <SelectItem value="semi-urban">Semi-Urban</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label style={{ color: "#504136" }}>Planned Amenities</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {featureOptions.map(feature => (
                        <div key={feature.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={feature.id}
                            checked={formData.features.includes(feature.id)}
                            onCheckedChange={() => toggleFeature(feature.id)}
                            data-testid={`feature-${feature.id}`}
                          />
                          <Label htmlFor={feature.id} className="font-normal cursor-pointer" style={{ color: "#4a7068" }}>
                            {feature.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Navigation for steps 1–2 */}
              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <Button variant="outline" onClick={() => setStep(step - 1)} style={{ borderColor: "#B2E6D4", color: "#4a7068" }} data-testid="prev-step-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                ) : <div />}
                <Button
                  onClick={handleNext}
                  style={{ backgroundColor: "#689689", color: "#faf9f6" }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = "#83E8BA"}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = "#689689"}
                  data-testid="next-step-btn"
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        )}

        {/* ── Step 3: Design — wide split layout ─────────────────────────── */}
        {isDesignStep && (
          <div className="space-y-3">
            {/* Compact header */}
            <div className="text-center">
              <h2 className="text-base font-semibold" style={{ color: "#504136" }}>Design Your Station</h2>
              <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>
                Fine-tune positions on the canvas · Drag to reposition · Handles to resize · Freeze when ready
              </p>
            </div>

            {/* Split layout: config panel + SVG canvas */}
            <div className="flex flex-col lg:flex-row gap-4 items-start">

              {/* ── Left: unified config card ── */}
              <div className="w-full lg:w-72 lg:flex-shrink-0 flex flex-col gap-3">
                <div className="rounded-xl shadow-sm divide-y" style={{ border: "1px solid #B2E6D4", backgroundColor: "#ffffff", divideColor: "#B2E6D4" }}>

                  {/* Charger summary row */}
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: "#4a7068" }}>Chargers</p>
                      <p className="text-lg font-bold font-mono leading-tight" style={{ color: "#689689" }}>
                        {getTotalChargers()} <span className="text-sm font-semibold" style={{ color: "#4a7068" }}>/ {getTotalPower()} kW</span>
                      </p>
                      {lengthInMeters > 0 && (
                        <p className="text-[11px] leading-tight" style={{ color: "#4a7068" }}>
                          Site fits up to {maxFittingChargers} charger{maxFittingChargers !== 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2"
                      style={{ color: "#689689" }}
                      onClick={addChargerConfig}
                      disabled={formData.charger_config.length >= CHARGER_OPTIONS.length}
                      data-testid="add-charger-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add type
                    </Button>
                  </div>

                  {/* Charger rows */}
                  <div className="divide-y" style={{ borderColor: "#B2E6D4" }}>
                    {formData.charger_config.map((config, index) => {
                      const info = CHARGER_OPTIONS.find(o => o.id === config.type);
                      const dotColor = CHARGER_TYPE_COLORS[config.type] || "#2563eb";
                      return (
                        <div
                          key={index}
                          className="px-4 py-2.5 flex items-center gap-2"
                          style={{ borderColor: "#B2E6D4" }}
                          data-testid={`charger-config-${index}`}
                        >
                          {/* Color dot */}
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: dotColor }}
                          />
                          {/* Type select */}
                          <div className="flex-1 min-w-0">
                            <Select
                              value={config.type}
                              onValueChange={v => updateChargerConfig(index, "type", v)}
                            >
                              <SelectTrigger className="border-0 shadow-none p-0 h-auto focus:ring-0 bg-transparent text-sm font-medium" style={{ color: "#504136" }}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CHARGER_OPTIONS.map(opt => (
                                  <SelectItem
                                    key={opt.id}
                                    value={opt.id}
                                    disabled={formData.charger_config.some((c, i) => i !== index && c.type === opt.id)}
                                  >
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-[11px] leading-tight" style={{ color: "#4a7068" }}>{info?.cost_range}</p>
                          </div>
                          {/* Qty stepper */}
                          <div className="flex items-center rounded-md overflow-hidden flex-shrink-0" style={{ border: "1px solid #B2E6D4" }}>
                            <button
                              type="button"
                              className="w-8 h-8 min-w-[2rem] flex items-center justify-center disabled:opacity-30 font-bold text-sm select-none"
                              style={{ color: "#4a7068" }}
                              onClick={() => updateChargerConfig(index, "quantity", Math.max(1, (config.quantity || 1) - 1))}
                              disabled={(config.quantity || 1) <= 1}
                              data-testid={`charger-qty-dec-${index}`}
                            >−</button>
                            <span
                              className="w-7 text-center font-mono font-semibold text-sm select-none"
                              style={{ color: "#504136" }}
                              data-testid={`charger-qty-${index}`}
                            >{config.quantity || 1}</span>
                            <button
                              type="button"
                              className="w-8 h-8 min-w-[2rem] flex items-center justify-center disabled:opacity-30 font-bold text-sm select-none"
                              style={{ color: "#4a7068" }}
                              onClick={() => updateChargerConfig(index, "quantity", Math.min(20, (config.quantity || 1) + 1))}
                              disabled={(config.quantity || 1) >= 20}
                              data-testid={`charger-qty-inc-${index}`}
                            >+</button>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="w-6 h-6 hover:text-red-500 flex-shrink-0"
                            style={{ color: "#4a7068" }}
                            onClick={() => removeChargerConfig(index)}
                            disabled={formData.charger_config.length <= 1}
                            data-testid={`remove-charger-${index}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Solar toggle row */}
                  <div className={`px-4 py-3 flex items-center gap-3 ${!solarFeasible ? "opacity-60" : ""}`} style={{ borderColor: "#B2E6D4" }}>
                    <Sun className="w-4 h-4 flex-shrink-0" style={{ color: solarFeasible ? "#A49E8D" : "#4a7068" }} />
                    <div className="flex-1">
                      <Label
                        htmlFor="solar_required"
                        className="text-sm font-medium cursor-pointer"
                        style={{ color: solarFeasible ? "#504136" : "#4a7068" }}
                      >
                        Solar Canopy
                      </Label>
                      {solarFeasible ? (
                        <p className="text-[11px] leading-tight" style={{ color: "#4a7068" }}>Toggle to show/hide on canvas</p>
                      ) : (
                        <p className="text-[11px] leading-tight" style={{ color: "#A49E8D" }}>Requires site ≥ 10m × 10m (≈33ft × 33ft)</p>
                      )}
                    </div>
                    <Checkbox
                      id="solar_required"
                      checked={formData.solar_required && solarFeasible}
                      onCheckedChange={v => solarFeasible && handleInputChange("solar_required", v)}
                      disabled={!solarFeasible}
                      data-testid="solar-required-checkbox"
                    />
                  </div>

                  {/* Budget row */}
                  <div className="px-4 py-3 space-y-1.5" style={{ borderColor: "#B2E6D4" }}>
                    <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#504136" }}>
                      Budget Range <span className="normal-case font-normal" style={{ color: "#4a7068" }}>(optional)</span>
                    </Label>
                    <Select
                      value={formData.budget_range}
                      onValueChange={v => handleInputChange("budget_range", v)}
                    >
                      <SelectTrigger className="h-8 text-sm" style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }} data-testid="budget-range-select">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_1cr">Under ₹1 Crore</SelectItem>
                        <SelectItem value="1_3cr">₹1–3 Crores</SelectItem>
                        <SelectItem value="3_5cr">₹3–5 Crores</SelectItem>
                        <SelectItem value="above_5cr">Above ₹5 Crores</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Land Lease row */}
                  <div className="px-4 py-3 space-y-1.5" style={{ borderColor: "#B2E6D4" }}>
                    <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#504136" }}>
                      Land Lease / Rental <span className="normal-case font-normal" style={{ color: "#4a7068" }}>(optional)</span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g., 50000"
                      className="h-8 text-sm"
                      style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                      value={formData.land_lease_cost_monthly}
                      onChange={e => handleInputChange("land_lease_cost_monthly", e.target.value)}
                    />
                    <p className="text-[11px] leading-tight" style={{ color: "#4a7068" }}>Monthly cost in ₹. Leave blank if you own the land.</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setStep(2)}
                    className="flex-shrink-0"
                    style={{ borderColor: "#B2E6D4", color: "#4a7068" }}
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={loading || !liveLayout}
                    className="flex-1"
                    style={{ backgroundColor: "#689689", color: "#faf9f6" }}
                    onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#83E8BA"; }}
                    onMouseLeave={e => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#689689"; }}
                    data-testid="create-project-btn"
                  >
                    {loading ? (
                      "Creating…"
                    ) : (
                      <>
                        <Lock className="w-3.5 h-3.5 mr-1.5" />
                        Freeze &amp; Create
                      </>
                    )}
                  </Button>
                </div>

                {!liveLayout && (
                  <p className="text-xs text-center" style={{ color: "#A49E8D" }}>
                    Go back to Step 2 and enter land dimensions to unlock the canvas.
                  </p>
                )}
              </div>

              {/* ── Right: live SVG canvas ── */}
              <div className="flex-1 min-w-0">
                <SiteLayoutMap
                  key={canvasKey}
                  layout={liveLayout}
                  siteInputs={{ land_length: lengthInMeters, land_width: widthInMeters }}
                  isRerunning={false}
                  onRerun={null}
                  onPositionsChange={pos => { frozenPositionsRef.current = pos; }}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-6" style={{ borderTop: "1px solid #B2E6D4", backgroundColor: "#ffffff" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-end">
          <Link
            to="/contact"
            className="text-sm transition-colors flex items-center gap-1.5"
            style={{ color: "#4a7068" }}
            onMouseEnter={e => e.currentTarget.style.color = "#689689"}
            onMouseLeave={e => e.currentTarget.style.color = "#4a7068"}
          >
            <span>Contact Us</span>
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default NewProject;
